<template>
  <div class="user-profile-container">
    <div class="profile-header">
      <div class="header-content">
        <el-button class="back-btn" type="text" icon="el-icon-arrow-left" @click="goBack">返回</el-button>
        <h1 class="page-title">个人信息</h1>
      </div>
    </div>

    <div class="profile-content">
      <el-card class="profile-card" v-loading="loading">
        <div slot="header" class="card-header">
          <span>基本信息</span>
          <el-button v-if="!isEditing" type="primary" size="small" @click="startEdit">编辑</el-button>
          <div v-else>
            <el-button size="small" @click="cancelEdit">取消</el-button>
            <el-button type="primary" size="small" :loading="saving" @click="saveProfile">保存</el-button>
          </div>
        </div>

        <el-form ref="profileForm" :model="form" :rules="rules" label-width="100px" :disabled="!isEditing">
          <el-form-item label="用户名" prop="username">
            <el-input v-model="form.username" placeholder="请输入用户名" :disabled="true"></el-input>
            <span class="form-tip">用户名不可修改</span>
          </el-form-item>

          <el-form-item label="真实姓名" prop="realName">
            <el-input v-model="form.realName" placeholder="请输入真实姓名"></el-input>
          </el-form-item>

          <el-form-item label="邮箱">
            <div class="field-with-action">
              <el-input v-model="form.email" placeholder="未绑定邮箱" :disabled="true"></el-input>
              <el-button type="text" @click="showChangeEmail">{{ form.email ? '修改' : '绑定' }}</el-button>
            </div>
          </el-form-item>

          <el-form-item label="手机号">
            <div class="field-with-action">
              <el-input v-model="form.phone" placeholder="未绑定手机号" :disabled="true"></el-input>
              <el-button type="text" @click="showChangePhone">{{ form.phone ? '修改' : '绑定' }}</el-button>
            </div>
          </el-form-item>

          <el-form-item label="性别" prop="sex">
            <el-radio-group v-model="form.sex">
              <el-radio :label="1">男</el-radio>
              <el-radio :label="0">女</el-radio>
            </el-radio-group>
          </el-form-item>

          <el-form-item label="学校" prop="school">
            <el-input v-model="form.school" placeholder="请输入学校名称"></el-input>
          </el-form-item>

          <el-form-item label="年级" prop="grade">
            <el-input v-model="form.grade" placeholder="请输入年级"></el-input>
          </el-form-item>
        </el-form>
      </el-card>

      <el-card class="profile-card account-card">
        <div slot="header" class="card-header">
          <span>账号安全</span>
        </div>

        <div class="account-item">
          <div class="item-info">
            <span class="item-label">修改密码</span>
            <span class="item-desc">定期修改密码可以保护账号安全</span>
          </div>
          <el-button type="text" @click="showChangePassword">修改</el-button>
        </div>

        <div class="account-item">
          <div class="item-info">
            <span class="item-label">注册时间</span>
            <span class="item-value">{{ formatDate(userInfo.createTime) }}</span>
          </div>
        </div>
      </el-card>

      <el-card v-if="isStudentUser" class="profile-card binding-card" v-loading="orgBindingsLoading">
        <div slot="header" class="card-header">
          <span>机构绑定</span>
        </div>

        <div class="org-bind-row">
          <el-input
            v-model="orgPromoCode"
            placeholder="请输入机构码"
            clearable
            @keyup.enter.native="handleBindOrgCode"
          ></el-input>
          <el-button type="primary" :loading="orgBinding" @click="handleBindOrgCode">
            绑定机构
          </el-button>
        </div>
        <div class="org-bind-tip">一个用户可绑定多个机构，重复绑定同一家机构会提示已绑定。</div>

        <div class="org-binding-list">
          <div v-if="!myOrganizations.length" class="org-empty">暂未绑定任何机构</div>
          <div v-else>
            <div v-for="item in myOrganizations" :key="item.id" class="org-binding-item">
              <div class="org-binding-main">
                <div class="org-binding-name">{{ item.orgName || '未命名机构' }}</div>
                <div class="org-binding-meta">
                  <span>机构码：{{ item.promoCode || '-' }}</span>
                  <span v-if="item.contactName">联系人：{{ item.contactName }}</span>
                  <span v-if="item.contactPhone">电话：{{ item.contactPhone }}</span>
                </div>
              </div>
              <div class="org-binding-time">{{ formatDateTime(item.bindTime) }}</div>
            </div>
          </div>
        </div>
      </el-card>
    </div>

    <!-- 修改邮箱对话框 -->
    <el-dialog :title="form.email ? '修改邮箱' : '绑定邮箱'" :visible.sync="emailDialogVisible" width="450px">
      <el-form ref="emailForm" :model="emailForm" :rules="emailRules" label-width="100px">
        <el-form-item label="新邮箱" prop="email">
          <el-input v-model="emailForm.email" placeholder="请输入新邮箱"></el-input>
        </el-form-item>
        <el-form-item label="验证码" prop="code">
          <div class="code-input-row">
            <el-input v-model="emailForm.code" placeholder="请输入验证码"></el-input>
            <el-button type="primary" :disabled="emailCountdown > 0" :loading="sendingEmailCode" @click="sendEmailCode">
              {{ emailCountdown > 0 ? `${emailCountdown}s后重发` : '发送验证码' }}
            </el-button>
          </div>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="emailDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="bindingEmail" @click="bindEmail">确定</el-button>
      </span>
    </el-dialog>

    <!-- 修改手机号对话框 -->
    <el-dialog :title="form.phone ? '修改手机号' : '绑定手机号'" :visible.sync="phoneDialogVisible" width="450px">
      <el-form ref="phoneForm" :model="phoneForm" :rules="phoneRules" label-width="100px">
        <el-form-item label="新手机号" prop="phone">
          <el-input v-model="phoneForm.phone" placeholder="请输入新手机号"></el-input>
        </el-form-item>
        <el-form-item label="验证码" prop="code">
          <div class="code-input-row">
            <el-input v-model="phoneForm.code" placeholder="请输入验证码"></el-input>
            <el-button type="primary" :disabled="phoneCountdown > 0" :loading="sendingPhoneCode" @click="sendPhoneCode">
              {{ phoneCountdown > 0 ? `${phoneCountdown}s后重发` : '发送验证码' }}
            </el-button>
          </div>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="phoneDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="bindingPhone" @click="bindPhoneNumber">确定</el-button>
      </span>
    </el-dialog>

    <!-- 修改密码对话框 -->
    <el-dialog title="修改密码" :visible.sync="passwordDialogVisible" width="400px">
      <el-form ref="passwordForm" :model="passwordForm" :rules="passwordRules" label-width="100px">
        <el-form-item label="原密码" prop="oldPassword">
          <el-input v-model="passwordForm.oldPassword" type="password" placeholder="请输入原密码" show-password></el-input>
        </el-form-item>
        <el-form-item label="新密码" prop="newPassword">
          <el-input v-model="passwordForm.newPassword" type="password" placeholder="请输入新密码" show-password></el-input>
        </el-form-item>
        <el-form-item label="确认密码" prop="confirmPassword">
          <el-input v-model="passwordForm.confirmPassword" type="password" placeholder="请再次输入新密码" show-password></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="passwordDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="changingPassword" @click="changePassword">确定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { getUserInfo, updateUserInfo, changePassword, sendBindEmailCode, bindEmail as bindEmailApi, sendBindPhoneCode, bindPhone as bindPhoneApi } from '@/api/user'
import { bindMyPromoCode, getMyOrganizations } from '@/api/organization'
import { Encrypt } from '@/utils/Secret'

export default {
  name: 'UserProfile',
  data() {
    const validateConfirmPassword = (rule, value, callback) => {
      if (value !== this.passwordForm.newPassword) {
        callback(new Error('两次输入的密码不一致'))
      } else {
        callback()
      }
    }
    return {
      loading: false,
      saving: false,
      isEditing: false,
      userInfo: {},
      form: {
        id: '',
        username: '',
        realName: '',
        email: '',
        phone: '',
        sex: 1,
        school: '',
        grade: ''
      },
      originalForm: {},
      rules: {
        realName: [
          { max: 50, message: '真实姓名不能超过50个字符', trigger: 'blur' }
        ],
        email: [
          { type: 'email', message: '请输入正确的邮箱格式', trigger: 'blur' }
        ],
        phone: [
          { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号格式', trigger: 'blur' }
        ]
      },
      passwordDialogVisible: false,
      changingPassword: false,
      passwordForm: {
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
      },
      passwordRules: {
        oldPassword: [
          { required: true, message: '请输入原密码', trigger: 'blur' }
        ],
        newPassword: [
          { required: true, message: '请输入新密码', trigger: 'blur' },
          { min: 6, message: '密码长度不能少于6位', trigger: 'blur' }
        ],
        confirmPassword: [
          { required: true, message: '请再次输入新密码', trigger: 'blur' },
          { validator: validateConfirmPassword, trigger: 'blur' }
        ]
      },
      emailRules: {
        email: [
          { required: true, message: '请输入邮箱', trigger: 'blur' },
          { type: 'email', message: '请输入正确的邮箱格式', trigger: 'blur' }
        ],
        code: [
          { required: true, message: '请输入验证码', trigger: 'blur' }
        ]
      },
      // 手机号相关
      phoneDialogVisible: false,
      sendingPhoneCode: false,
      bindingPhone: false,
      phoneCountdown: 0,
      phoneForm: {
        phone: '',
        code: ''
      },
      orgPromoCode: '',
      orgBinding: false,
      orgBindingsLoading: false,
      myOrganizations: [],
      phoneRules: {
        phone: [
          { required: true, message: '请输入手机号', trigger: 'blur' },
          { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号格式', trigger: 'blur' }
        ],
        code: [
          { required: true, message: '请输入验证码', trigger: 'blur' }
        ]
      }
    }
  },
  created() {
    this.loadUserInfo()
  },
  computed: {
    isStudentUser() {
      return Number(this.userInfo && this.userInfo.roleId) === 1
    }
  },
  methods: {
    goBack() {
      this.$router.push('/')
    },
    async loadUserInfo() {
      this.loading = true
      try {
        // 先从 localStorage 获取基本信息
        const localUserInfo = localStorage.getItem('userInfo')
        if (localUserInfo) {
          this.userInfo = JSON.parse(localUserInfo)
          this.fillForm(this.userInfo)
        }
        // 再从服务器获取最新信息
        const res = await getUserInfo()
        if (res && res.data) {
          this.userInfo = res.data
          this.fillForm(res.data)
          // 更新 localStorage
          localStorage.setItem('userInfo', JSON.stringify(res.data))
          if (this.isStudentUser) {
            await this.loadMyOrganizations()
          } else {
            this.myOrganizations = []
          }
        }
      } catch (e) {
        console.error('获取用户信息失败:', e)
        this.$message.error('获取用户信息失败')
      } finally {
        this.loading = false
      }
    },
    fillForm(data) {
      // 处理性别字段：后端返回 gender (字符串 "男"/"女" 或空)，前端使用 sex (数字 1/0)
      let sexValue = 1 // 默认男
      if (data.sex !== undefined && data.sex !== null) {
        sexValue = data.sex
      } else if (data.gender) {
        sexValue = data.gender === '男' ? 1 : 0
      }

      this.form = {
        id: data.id || '',
        username: data.userName || data.username || '',
        realName: data.realName || '',
        email: data.email || '',
        phone: data.phone || '',
        sex: sexValue,
        school: data.school || '',
        grade: data.gradeName || data.grade || ''
      }
      this.originalForm = { ...this.form }
    },
    startEdit() {
      this.isEditing = true
    },
    cancelEdit() {
      this.form = { ...this.originalForm }
      this.isEditing = false
    },
    async saveProfile() {
      try {
        await this.$refs.profileForm.validate()
      } catch (e) {
        return
      }

      this.saving = true
      try {
        const updateData = {
          id: this.form.id,
          realName: this.form.realName,
          email: this.form.email,
          phone: this.form.phone,
          sex: this.form.sex,
          school: this.form.school,
          grade: this.form.grade
        }
        const res = await updateUserInfo(updateData)
        if (res && (res.code === 1 || res.code === 200)) {
          this.$message.success('保存成功')
          this.originalForm = { ...this.form }
          this.isEditing = false
          // 更新 localStorage
          const newUserInfo = { ...this.userInfo, ...updateData }
          localStorage.setItem('userInfo', JSON.stringify(newUserInfo))
          this.userInfo = newUserInfo
        } else {
          this.$message.error(res?.msg || '保存失败')
        }
      } catch (e) {
        console.error('保存用户信息失败:', e)
        this.$message.error('保存失败')
      } finally {
        this.saving = false
      }
    },
    showChangePassword() {
      this.passwordForm = {
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
      }
      this.passwordDialogVisible = true
    },
    async changePassword() {
      try {
        await this.$refs.passwordForm.validate()
      } catch (e) {
        return
      }

      this.changingPassword = true
      try {
        const res = await changePassword({
          id: this.form.id,
          oldPassword: Encrypt(this.passwordForm.oldPassword),
          password: Encrypt(this.passwordForm.newPassword)
        })
        if (res && (res.code === 1 || res.code === 200)) {
          this.$message.success('密码修改成功')
          this.passwordDialogVisible = false
        } else {
          this.$message.error(res?.msg || '密码修改失败')
        }
      } catch (e) {
        console.error('修改密码失败:', e)
        this.$message.error('密码修改失败')
      } finally {
        this.changingPassword = false
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return '-'
      const date = new Date(dateStr)
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
    },

    formatDateTime(value) {
      if (!value) return '-'
      const s = String(value)
      return s.length >= 19 ? s.slice(0, 19).replace('T', ' ') : s.replace('T', ' ')
    },

    async loadMyOrganizations() {
      if (!this.isStudentUser) {
        this.myOrganizations = []
        return
      }
      this.orgBindingsLoading = true
      try {
        const res = await getMyOrganizations()
        this.myOrganizations = (res && res.code === 1 && Array.isArray(res.data)) ? res.data : []
      } catch (e) {
        console.error('加载机构绑定列表失败:', e)
        this.myOrganizations = []
      } finally {
        this.orgBindingsLoading = false
      }
    },

    async handleBindOrgCode() {
      if (!this.isStudentUser) {
        return
      }
      const code = String(this.orgPromoCode || '').trim()
      if (!code) {
        this.$message.warning('请输入机构码')
        return
      }
      this.orgBinding = true
      try {
        const res = await bindMyPromoCode(code)
        if (res && (res.code === 1 || res.code === 200)) {
          this.$message.success(res.msg || '机构绑定成功')
          this.orgPromoCode = ''
          await this.loadMyOrganizations()
        } else {
          this.$message.error((res && res.msg) || '机构绑定失败')
        }
      } catch (e) {
        console.error('机构绑定失败:', e)
        this.$message.error('机构绑定失败，请重试')
      } finally {
        this.orgBinding = false
      }
    },

    // 邮箱相关方法
    showChangeEmail() {
      this.emailForm = { email: '', code: '' }
      this.emailCountdown = 0
      this.emailDialogVisible = true
    },
    async sendEmailCode() {
      if (!this.emailForm.email) {
        this.$message.warning('请先输入邮箱')
        return
      }
      // 验证邮箱格式
      const emailReg = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailReg.test(this.emailForm.email)) {
        this.$message.warning('请输入正确的邮箱格式')
        return
      }
      this.sendingEmailCode = true
      try {
        const res = await sendBindEmailCode(this.emailForm.email)
        if (res && (res.code === 1 || res.code === 200)) {
          this.$message.success('验证码已发送到您的邮箱')
          this.startEmailCountdown()
        } else {
          this.$message.error(res?.msg || '发送验证码失败')
        }
      } catch (e) {
        console.error('发送邮箱验证码失败:', e)
        this.$message.error('发送验证码失败')
      } finally {
        this.sendingEmailCode = false
      }
    },
    startEmailCountdown() {
      this.emailCountdown = 60
      const timer = setInterval(() => {
        this.emailCountdown--
        if (this.emailCountdown <= 0) {
          clearInterval(timer)
        }
      }, 1000)
    },
    async bindEmail() {
      try {
        await this.$refs.emailForm.validate()
      } catch (e) {
        return
      }
      this.bindingEmail = true
      try {
        const res = await bindEmailApi(this.emailForm.email, this.emailForm.code)
        if (res && (res.code === 1 || res.code === 200)) {
          this.$message.success('邮箱绑定成功')
          this.emailDialogVisible = false
          // 更新本地数据
          this.form.email = this.emailForm.email
          this.originalForm.email = this.emailForm.email
          this.userInfo.email = this.emailForm.email
          localStorage.setItem('userInfo', JSON.stringify(this.userInfo))
        } else {
          this.$message.error(res?.msg || '绑定失败')
        }
      } catch (e) {
        console.error('绑定邮箱失败:', e)
        this.$message.error('绑定失败')
      } finally {
        this.bindingEmail = false
      }
    },
    // 手机号相关方法
    showChangePhone() {
      this.phoneForm = { phone: '', code: '' }
      this.phoneCountdown = 0
      this.phoneDialogVisible = true
    },
    async sendPhoneCode() {
      if (!this.phoneForm.phone) {
        this.$message.warning('请先输入手机号')
        return
      }
      // 验证手机号格式
      const phoneReg = /^1[3-9]\d{9}$/
      if (!phoneReg.test(this.phoneForm.phone)) {
        this.$message.warning('请输入正确的手机号格式')
        return
      }
      this.sendingPhoneCode = true
      try {
        const res = await sendBindPhoneCode(this.phoneForm.phone)
        if (res && (res.code === 1 || res.code === 200)) {
          this.$message.success('验证码已发送到您的手机')
          this.startPhoneCountdown()
        } else {
          this.$message.error(res?.msg || '发送验证码失败')
        }
      } catch (e) {
        console.error('发送手机验证码失败:', e)
        this.$message.error('发送验证码失败')
      } finally {
        this.sendingPhoneCode = false
      }
    },
    startPhoneCountdown() {
      this.phoneCountdown = 60
      const timer = setInterval(() => {
        this.phoneCountdown--
        if (this.phoneCountdown <= 0) {
          clearInterval(timer)
        }
      }, 1000)
    },
    async bindPhoneNumber() {
      try {
        await this.$refs.phoneForm.validate()
      } catch (e) {
        return
      }
      this.bindingPhone = true
      try {
        const res = await bindPhoneApi(this.phoneForm.phone, this.phoneForm.code)
        if (res && (res.code === 1 || res.code === 200)) {
          this.$message.success('手机号绑定成功')
          this.phoneDialogVisible = false
          // 更新本地数据
          this.form.phone = this.phoneForm.phone
          this.originalForm.phone = this.phoneForm.phone
          this.userInfo.phone = this.phoneForm.phone
          localStorage.setItem('userInfo', JSON.stringify(this.userInfo))
        } else {
          this.$message.error(res?.msg || '绑定失败')
        }
      } catch (e) {
        console.error('绑定手机号失败:', e)
        this.$message.error('绑定失败')
      } finally {
        this.bindingPhone = false
      }
    }
  }
}
</script>

<style scoped>
.user-profile-container {
  min-height: 100vh;
  background: #f5f7fa;
}

.profile-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px 0;
}

.header-content {
  max-width: 800px;
  margin: 0 auto;
  padding: 0 24px;
  display: flex;
  align-items: center;
  gap: 16px;
}

.back-btn {
  color: #fff;
  font-size: 16px;
}

.back-btn:hover {
  color: rgba(255, 255, 255, 0.8);
}

.page-title {
  color: #fff;
  font-size: 24px;
  font-weight: 600;
  margin: 0;
}

.profile-content {
  max-width: 800px;
  margin: 0 auto;
  padding: 24px;
}

.profile-card {
  margin-bottom: 24px;
}

.binding-card {
  margin-bottom: 24px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 16px;
  font-weight: 600;
}

.form-tip {
  font-size: 12px;
  color: #999;
  margin-left: 8px;
}

.field-with-action {
  display: flex;
  align-items: center;
  gap: 8px;
}

.field-with-action .el-input {
  flex: 1;
}

.org-bind-row {
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 12px;
}

.org-bind-row .el-input {
  flex: 1;
}

.org-bind-tip {
  font-size: 12px;
  color: #909399;
  margin-bottom: 14px;
}

.org-binding-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.org-binding-item {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 16px;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  background: #fafafa;
}

.org-binding-main {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.org-binding-name {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

.org-binding-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 12px;
  color: #909399;
}

.org-binding-time {
  flex-shrink: 0;
  font-size: 12px;
  color: #909399;
}

.org-empty {
  font-size: 13px;
  color: #909399;
  text-align: center;
  padding: 12px 0;
}

.code-input-row {
  display: flex;
  gap: 12px;
}

.code-input-row .el-input {
  flex: 1;
}

.code-input-row .el-button {
  white-space: nowrap;
}

.account-card .account-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
  border-bottom: 1px solid #f0f0f0;
}

.account-card .account-item:last-child {
  border-bottom: none;
}

.item-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.item-label {
  font-size: 14px;
  color: #333;
}

.item-desc {
  font-size: 12px;
  color: #999;
}

.item-value {
  font-size: 14px;
  color: #666;
}

::v-deep .el-form-item {
  margin-bottom: 22px;
}

::v-deep .el-input.is-disabled .el-input__inner {
  background-color: #f5f7fa;
  color: #909399;
}
</style>
